import {
  BULK_TRANSFER_CSV_COLUMNS,
  BULK_TRANSFER_ROW_STATUS_FAILED,
  BULK_TRANSFER_ROW_STATUS_NOT_PROCESSED,
  BULK_TRANSFER_ROW_STATUS_SUCCEEDED,
  CSV_BOOLEAN_FALSE_VALUES,
  CSV_BOOLEAN_TRUE_VALUES,
  CSV_COLUMN_AMOUNT,
  CSV_COLUMN_BENEFICIARY_ACCOUNT_NUMBER,
  CSV_COLUMN_BENEFICIARY_NAME,
  CSV_COLUMN_CREATE_CREDIT_RECORD,
  CSV_COLUMN_CURRENCY,
  CSV_COLUMN_PURPOSE,
  CSV_COLUMN_REFERENCE,
  CSV_COLUMN_SOURCE_ACCOUNT_NUMBER,
  CURRENCY_CODE_PATTERN,
  MAX_BULK_TRANSFER_ROWS,
} from 'modules/partner-account-transfer/constants/constants';

const BYTE_ORDER_MARK = '\uFEFF';
const CARRIAGE_RETURN = '\r';
const COMMA = ',';
const DOUBLE_QUOTE = '"';
const NEW_LINE = '\n';

const FIRST_DATA_ROW_NUMBER = 2;

const CREATE_CREDIT_RECORD_DEFAULT = false;

export type BulkTransferPayloadRow = {
  source_account_number: string;
  beneficiary_name?: string;
  beneficiary_account_number: string;
  amount: number;
  currency: string;
  purpose: string;
  reference?: string;
  create_credit_record: boolean;
};

export type ParsedBulkTransferRow = {
  rowNumber: number;
  transfer: BulkTransferPayloadRow;
  errors: string[];
};

export type ParsedBulkTransferFile = {
  rows: ParsedBulkTransferRow[];
  fileError: string;
};

export type BulkTransferResultRow = {
  rowNumber: number;
  sourceAccountNumber: string;
  beneficiaryAccountNumber: string;
  amount: number;
  currency: string;
  createCreditRecord: boolean;
  status: string;
  referenceId: string;
  partnerReferenceId: string;
  bankReferenceId: string;
  error: string;
};

export const parseCsv = (content: string): string[][] => {
  const text = content.startsWith(BYTE_ORDER_MARK) ? content.slice(1) : content;
  const rows: string[][] = [];

  let currentRow: string[] = [];
  let currentField = '';
  let isInsideQuotes = false;

  const finishRow = () => {
    currentRow.push(currentField);
    rows.push(currentRow);
    currentField = '';
    currentRow = [];
  };

  for (let index = 0; index < text.length; index += 1) {
    const character = text[index];

    if (isInsideQuotes) {
      if (character !== DOUBLE_QUOTE) {
        currentField += character;
      } else if (text[index + 1] === DOUBLE_QUOTE) {
        currentField += DOUBLE_QUOTE;
        index += 1;
      } else {
        isInsideQuotes = false;
      }
    } else if (character === DOUBLE_QUOTE) {
      isInsideQuotes = true;
    } else if (character === COMMA) {
      currentRow.push(currentField);
      currentField = '';
    } else if (character === NEW_LINE) {
      finishRow();
    } else if (character === CARRIAGE_RETURN) {
      if (text[index + 1] === NEW_LINE) {
        index += 1;
      }

      finishRow();
    } else {
      currentField += character;
    }
  }

  if (currentField !== '' || currentRow.length > 0) {
    finishRow();
  }

  return rows;
};

const isBlankCsvRow = (cells: string[]): boolean => cells.every((cell) => cell.trim() === '');

const getCellValue = (headers: string[], cells: string[], columnKey: string): string => {
  const columnIndex = headers.indexOf(columnKey);

  if (columnIndex === -1) {
    return '';
  }

  return (cells[columnIndex] ?? '').trim();
};

const parseAmount = (rawAmount: string): number => {
  if (rawAmount === '') {
    return Number.NaN;
  }

  return Number(rawAmount.replace(/,/g, ''));
};

const parseBooleanCell = (rawValue: string, defaultValue: boolean): boolean | null => {
  const normalizedValue = rawValue.toLowerCase();

  if (normalizedValue === '') {
    return defaultValue;
  }

  if (CSV_BOOLEAN_TRUE_VALUES.includes(normalizedValue)) {
    return true;
  }

  if (CSV_BOOLEAN_FALSE_VALUES.includes(normalizedValue)) {
    return false;
  }

  return null;
};

const buildParsedRow = (headers: string[], cells: string[], rowNumber: number): ParsedBulkTransferRow => {
  const sourceAccountNumber = getCellValue(headers, cells, CSV_COLUMN_SOURCE_ACCOUNT_NUMBER);
  const beneficiaryName = getCellValue(headers, cells, CSV_COLUMN_BENEFICIARY_NAME);
  const beneficiaryAccountNumber = getCellValue(headers, cells, CSV_COLUMN_BENEFICIARY_ACCOUNT_NUMBER);
  const rawAmount = getCellValue(headers, cells, CSV_COLUMN_AMOUNT);
  const currency = getCellValue(headers, cells, CSV_COLUMN_CURRENCY).toUpperCase();
  const purpose = getCellValue(headers, cells, CSV_COLUMN_PURPOSE);
  const reference = getCellValue(headers, cells, CSV_COLUMN_REFERENCE);
  const rawCreateCreditRecord = getCellValue(headers, cells, CSV_COLUMN_CREATE_CREDIT_RECORD);
  const createCreditRecord = parseBooleanCell(rawCreateCreditRecord, CREATE_CREDIT_RECORD_DEFAULT);
  const amount = parseAmount(rawAmount);
  const errors: string[] = [];

  if (cells.length !== headers.length) {
    errors.push(`Expected ${headers.length} columns but found ${cells.length}`);
  }

  if (sourceAccountNumber === '') {
    errors.push(`${CSV_COLUMN_SOURCE_ACCOUNT_NUMBER} is required`);
  }

  if (beneficiaryAccountNumber === '') {
    errors.push(`${CSV_COLUMN_BENEFICIARY_ACCOUNT_NUMBER} is required`);
  }

  if (rawAmount === '') {
    errors.push(`${CSV_COLUMN_AMOUNT} is required`);
  } else if (Number.isNaN(amount) || !Number.isFinite(amount)) {
    errors.push(`${CSV_COLUMN_AMOUNT} "${rawAmount}" is not a number`);
  } else if (amount <= 0) {
    errors.push(`${CSV_COLUMN_AMOUNT} must be greater than 0`);
  }

  if (currency === '') {
    errors.push(`${CSV_COLUMN_CURRENCY} is required`);
  } else if (!CURRENCY_CODE_PATTERN.test(currency)) {
    errors.push(`${CSV_COLUMN_CURRENCY} "${currency}" is not a valid currency code`);
  }

  if (purpose === '') {
    errors.push(`${CSV_COLUMN_PURPOSE} is required`);
  }

  if (createCreditRecord === null) {
    errors.push(
      `${CSV_COLUMN_CREATE_CREDIT_RECORD} "${rawCreateCreditRecord}" must be one of ${[
        ...CSV_BOOLEAN_TRUE_VALUES,
        ...CSV_BOOLEAN_FALSE_VALUES,
      ].join(', ')}`
    );
  }

  const transfer: BulkTransferPayloadRow = {
    source_account_number: sourceAccountNumber,
    beneficiary_account_number: beneficiaryAccountNumber,
    amount,
    currency,
    purpose,
    create_credit_record: createCreditRecord ?? CREATE_CREDIT_RECORD_DEFAULT,
  };

  if (beneficiaryName !== '') {
    transfer.beneficiary_name = beneficiaryName;
  }

  if (reference !== '') {
    transfer.reference = reference;
  }

  return { rowNumber, transfer, errors };
};

export const parseBulkTransferCsv = (content: string): ParsedBulkTransferFile => {
  const csvRows = parseCsv(content).filter((cells) => !isBlankCsvRow(cells));

  if (csvRows.length === 0) {
    return { rows: [], fileError: 'The selected file is empty.' };
  }

  const headers = csvRows[0].map((header) => header.trim().toLowerCase());
  const missingHeaders = BULK_TRANSFER_CSV_COLUMNS.filter(
    (column) => column.isRequired && !headers.includes(column.key)
  ).map((column) => column.key);

  if (missingHeaders.length > 0) {
    return { rows: [], fileError: `Missing required column(s): ${missingHeaders.join(', ')}.` };
  }

  const dataRows = csvRows.slice(1);

  if (dataRows.length === 0) {
    return { rows: [], fileError: 'The selected file has a header row but no transfers.' };
  }

  if (dataRows.length > MAX_BULK_TRANSFER_ROWS) {
    return {
      rows: [],
      fileError: `A file can hold at most ${MAX_BULK_TRANSFER_ROWS} transfers, but this one has ${dataRows.length}.`,
    };
  }

  const rows = dataRows.map((cells, index) => buildParsedRow(headers, cells, index + FIRST_DATA_ROW_NUMBER));

  return { rows, fileError: '' };
};

export const getInvalidRows = (rows: ParsedBulkTransferRow[]): ParsedBulkTransferRow[] =>
  rows.filter((row) => row.errors.length > 0);

export const chunkRows = <T>(rows: T[], chunkSize: number): T[][] => {
  const chunks: T[][] = [];

  for (let index = 0; index < rows.length; index += chunkSize) {
    chunks.push(rows.slice(index, index + chunkSize));
  }

  return chunks;
};

export const buildNotProcessedResult = (row: ParsedBulkTransferRow, error: string): BulkTransferResultRow => ({
  rowNumber: row.rowNumber,
  sourceAccountNumber: row.transfer.source_account_number,
  beneficiaryAccountNumber: row.transfer.beneficiary_account_number,
  amount: row.transfer.amount,
  currency: row.transfer.currency,
  createCreditRecord: row.transfer.create_credit_record,
  status: BULK_TRANSFER_ROW_STATUS_NOT_PROCESSED,
  referenceId: '',
  partnerReferenceId: '',
  bankReferenceId: '',
  error,
});

export const buildFailedResult = (row: ParsedBulkTransferRow, error: string): BulkTransferResultRow => ({
  ...buildNotProcessedResult(row, error),
  status: BULK_TRANSFER_ROW_STATUS_FAILED,
});

export const buildResultsForChunk = (chunk: ParsedBulkTransferRow[], apiResults: any[]): BulkTransferResultRow[] => {
  return chunk.map((row, indexInChunk) => {
    const apiResult = apiResults.find((result) => result?.index === indexInChunk);

    if (!apiResult) {
      return buildNotProcessedResult(row, 'The server did not report an outcome for this row.');
    }

    return {
      rowNumber: row.rowNumber,
      sourceAccountNumber: apiResult.source_account_number ?? row.transfer.source_account_number,
      beneficiaryAccountNumber: apiResult.beneficiary_account_number ?? row.transfer.beneficiary_account_number,
      amount: apiResult.amount ?? row.transfer.amount,
      currency: apiResult.currency ?? row.transfer.currency,
      createCreditRecord: apiResult.create_credit_record ?? row.transfer.create_credit_record,
      status: apiResult.status ?? BULK_TRANSFER_ROW_STATUS_FAILED,
      referenceId: apiResult.reference_id ?? '',
      partnerReferenceId: apiResult.partner_reference_id ?? '',
      bankReferenceId: apiResult.bank_reference_id ?? '',
      error: apiResult.error_code ?? '',
    };
  });
};

const escapeCsvValue = (value: string): string => {
  if (!/[",\r\n]/.test(value)) {
    return value;
  }

  return `"${value.replace(/"/g, '""')}"`;
};

const toCsvContent = (rows: string[][]): string =>
  rows.map((cells) => cells.map(escapeCsvValue).join(COMMA)).join('\n');

export const buildSampleCsvContent = (): string =>
  toCsvContent([
    BULK_TRANSFER_CSV_COLUMNS.map((column) => column.key),
    [
      'GB33BUKB20201555555555',
      'Acme Ltd',
      'GB94BARC10201530093459',
      '1500.50',
      'USD',
      'Supplier payment',
      'INV-1001',
      'true',
    ],
    [
      'GB33BUKB20201555555555',
      'Globex, Inc.',
      'GB94BARC10201530093460',
      '250',
      'USD',
      'Vendor payout',
      'INV-1002',
      'false',
    ],
  ]);

export const buildResultsCsvContent = (results: BulkTransferResultRow[]): string =>
  toCsvContent([
    [
      'row_number',
      CSV_COLUMN_SOURCE_ACCOUNT_NUMBER,
      CSV_COLUMN_BENEFICIARY_ACCOUNT_NUMBER,
      CSV_COLUMN_AMOUNT,
      CSV_COLUMN_CURRENCY,
      CSV_COLUMN_CREATE_CREDIT_RECORD,
      'status',
      'reference_id',
      'partner_reference_id',
      'bank_reference_id',
      'error',
    ],
    ...results.map((result) => [
      String(result.rowNumber),
      result.sourceAccountNumber,
      result.beneficiaryAccountNumber,
      String(result.amount),
      result.currency,
      String(result.createCreditRecord),
      result.status,
      result.referenceId,
      result.partnerReferenceId,
      result.bankReferenceId,
      result.error,
    ]),
  ]);

export const downloadCsvFile = (fileName: string, content: string): void => {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = objectUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(objectUrl);
};

export const readFileAsText = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(reader.error ?? new Error('The file could not be read.'));
    reader.readAsText(file);
  });

export const isSucceededStatus = (status: string): boolean => status === BULK_TRANSFER_ROW_STATUS_SUCCEEDED;
